import MediaImage from "@/components/platform/MediaImage";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarCheck, CheckCircle2, MapPin, XCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { StatCard, SectionHeader } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";
import { useAdminEvents, useSetEventStatus, type AdminEvent } from "@/hooks/admin/useAdminGovernance";

const tone: Record<string, string> = {
  published: "bg-emerald-600 text-white",
  draft: "bg-amber-600 text-white",
  cancelled: "bg-red-600 text-white",
  archived: "bg-muted text-foreground",
};

const EventApprovals = () => {
  const { data: events = [], isLoading, error, refetch } = useAdminEvents();
  const setStatus = useSetEventStatus();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"pending" | "published" | "all">("pending");

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    return events
      .filter((e) =>
        tab === "all" ? true : tab === "published" ? e.status === "published" : e.status !== "published" && e.status !== "cancelled",
      )
      .filter((e) => (!t ? true : `${e.title} ${e.city ?? ""} ${e.venue_name ?? ""}`.toLowerCase().includes(t)));
  }, [events, tab, search]);

  const act = (event: AdminEvent, status: string) =>
    setStatus.mutate(
      { event, status },
      {
        onSuccess: () => toast.success(status === "published" ? "Event approved and published" : "Event rejected"),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Could not update the event"),
      },
    );

  if (isLoading) return <LoadingState label="Loading events…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Event approvals"
        description="Review events submitted by venues and creators before they appear publicly on the platform."
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total events" value={events.length} icon={CalendarCheck} />
        <StatCard label="Awaiting approval" value={events.filter((e) => e.status === "draft").length} tone="warning" />
        <StatCard label="Live" value={events.filter((e) => e.status === "published").length} tone="positive" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="published">Published</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events…" className="w-64" />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={CalendarCheck} title="Nothing to review" description="Submitted events will appear here." />
      ) : (
        <div className="space-y-3">
          {rows.map((e) => (
            <Card key={e.id} className="bg-card border-border">
              <CardContent className="p-4 flex flex-wrap items-center gap-4">
                {e.flyer_url && (
                  <MediaImage src={e.flyer_url} alt={`${e.title} flyer`} className="h-14 w-14 rounded-md object-cover" loading="lazy" />
                )}
                <div className="min-w-[220px] flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-foreground">{e.title}</p>
                    <Badge className={`${tone[e.status] ?? "bg-muted"} border-0 capitalize`}>{e.status}</Badge>
                    <Badge variant="outline" className="capitalize">{e.owner_type}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                    <MapPin className="w-3 h-3" /> {e.venue_name || e.city || "Location TBC"} ·{" "}
                    {format(new Date(e.event_date), "d MMM yyyy")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {e.status !== "published" && (
                    <Button size="sm" className="gap-1" disabled={setStatus.isPending} onClick={() => act(e, "published")}>
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </Button>
                  )}
                  {e.status !== "cancelled" && (
                    <Button size="sm" variant="outline" className="gap-1 text-red-500" disabled={setStatus.isPending} onClick={() => act(e, "cancelled")}>
                      <XCircle className="w-3 h-3" /> Reject
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventApprovals;
